/**
 * StaffEditPage — operational User + StaffProfile detail view with inline
 * edit form and audit log of edits.
 *
 * The page itself is layout only: load/edit state lives in `useStaffEditForm`,
 * and each card is its own component.
 */
import { Loader2, AlertCircle } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';

import StaffEditBody from './components/StaffEditBody';
import StaffEditHeader from './StaffEditHeader';
import { useStaffEditForm } from './useStaffEditForm';

export default function StaffEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const f = useStaffEditForm(id);

  if (f.loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={28} className="animate-spin text-stone-400" />
      </div>
    );
  }

  const member = f.member;
  if (f.loadError || !member) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12 text-center">
        <AlertCircle size={32} className="mx-auto text-red-400 mb-3" />
        <p className="text-stone-600 dark:text-stone-400">
          {f.loadError ?? 'Staff member not found'}
        </p>
        <button
          onClick={() => navigate('/admin/staff')}
          className="mt-4 text-sm text-moss-600 hover:text-moss-700"
        >
          ← Back to Staff
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <StaffEditHeader
        member={member}
        isEditing={f.isEditing}
        saving={f.saving}
        onBack={() => navigate('/admin/staff')}
        onStartEdit={f.startEdit}
        onCancel={f.cancelEdit}
        onSave={f.saveEdit}
      />

      {f.saveError && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-700 p-3 text-sm text-red-800 dark:text-red-200"
        >
          {f.saveError}
        </div>
      )}

      <StaffEditBody
        member={member}
        isEditing={f.isEditing}
        regions={f.regions}
        fieldErrors={f.fieldErrors}
        valueOf={f.valueOf}
        setField={f.setField}
        onSalariedChanged={f.refreshMember}
      />
    </div>
  );
}
